import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PageState } from "../../components/PageState";
import { useAuth } from "../../auth/AuthContext";
import { formatMoneyBR, pct } from "../../utils/format";

type SelectOption = { id: number; label: string };

type BudgetMonth = {
  month: number;
  budget: number;
  actual: number;
  diff: number;
  executionPct: number;
};

type BudgetSummary = {
  year: number;
  totals: {
    budget: number;
    actual: number;
    diff: number;
    executionPct: number;
  };
  months: BudgetMonth[];
};

type BudgetDetailAccount = {
  id: number | null;
  code: string | null;
  name: string;
  budget: number;
  actual: number;
};

type BudgetDetailCategory = {
  id: number | null;
  name: string;
  budget: number;
  actual: number;
  accounts: BudgetDetailAccount[];
};

type BudgetDetails = {
  year: number;
  month: number;
  totalBudget: number;
  totalActual: number;
  categories: BudgetDetailCategory[];
};

const monthLabels = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const normalizeYearInput = (value: string) =>
  value.replace(/\D/g, "").slice(0, 4);

const isValidYear = (value: string) => {
  if (value.length !== 4) return false;
  const year = Number(value);
  if (Number.isNaN(year)) return false;
  return year <= new Date().getFullYear() + 15;
};

const buildEmptyMonths = () =>
  Array.from({ length: 12 }, (_, index) => ({
    month: index + 1,
    budget: 0,
    actual: 0,
    diff: 0,
    executionPct: 0,
  }));

const formatMoneyShort = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return formatMoneyBR(value);
};

export const BudgetDashboardPage: React.FC = () => {
  const { client } = useAuth();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [accountId, setAccountId] = useState<number | "">("");
  const [costCenterId, setCostCenterId] = useState<number | "">("");
  const [accounts, setAccounts] = useState<SelectOption[]>([]);
  const [costCenters, setCostCenters] = useState<SelectOption[]>([]);
  const [data, setData] = useState<BudgetSummary | null>(null);
  const [expandedMonth, setExpandedMonth] = useState<number | null>(null);
  const [detailsByMonth, setDetailsByMonth] = useState<Record<number, BudgetDetails | null>>({});
  const [detailsLoading, setDetailsLoading] = useState<Record<number, boolean>>({});
  const [detailsError, setDetailsError] = useState<Record<number, string | null>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pctFormatter = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        style: "percent",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  useEffect(() => {
    client.get("/modules/accounts?take=200&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; code: string; name: string }>;
      };
      setAccounts(
        payload.data.map((item) => ({ id: item.id, label: `${item.code} - ${item.name}` }))
      );
    });
    client.get("/modules/cost-centers?take=200&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; code?: string | null; name: string }>;
      };
      setCostCenters(
        payload.data.map((item) => ({
          id: item.id,
          label: `${item.code ?? ""} ${item.name}`.trim(),
        }))
      );
    });
  }, [client]);

  const loadSummary = useCallback(async () => {
    if (!isValidYear(year)) {
      setError("Ano invalido.");
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setExpandedMonth(null);
    setDetailsByMonth({});
    setDetailsLoading({});
    setDetailsError({});
    const params = new URLSearchParams({ year });
    if (accountId) params.set("accountId", String(accountId));
    if (costCenterId) params.set("costCenterId", String(costCenterId));
    const response = await client.get(`/modules/budget/summary?${params.toString()}`);
    if (!response.ok) {
      setError("Falha ao carregar orcamento.");
      setData(null);
      setLoading(false);
      return;
    }
    const payload = (await response.json()) as { data?: BudgetSummary };
    setData(payload.data ?? null);
    setLoading(false);
  }, [client, year, accountId, costCenterId]);

  const loadDetails = useCallback(
    async (month: number) => {
      if (!isValidYear(year)) {
        setDetailsError((prev) => ({ ...prev, [month]: "Ano invalido." }));
        setDetailsLoading((prev) => ({ ...prev, [month]: false }));
        return;
      }
      setDetailsLoading((prev) => ({ ...prev, [month]: true }));
      setDetailsError((prev) => ({ ...prev, [month]: null }));
      const params = new URLSearchParams({ year, month: String(month) });
      if (accountId) params.set("accountId", String(accountId));
      if (costCenterId) params.set("costCenterId", String(costCenterId));
      const response = await client.get(`/modules/budget/details?${params.toString()}`);
      if (!response.ok) {
        setDetailsError((prev) => ({ ...prev, [month]: "Falha ao carregar detalhes." }));
        setDetailsByMonth((prev) => ({ ...prev, [month]: null }));
        setDetailsLoading((prev) => ({ ...prev, [month]: false }));
        return;
      }
      const payload = (await response.json()) as { data?: BudgetDetails };
      setDetailsByMonth((prev) => ({ ...prev, [month]: payload.data ?? null }));
      setDetailsLoading((prev) => ({ ...prev, [month]: false }));
    },
    [accountId, client, costCenterId, year]
  );

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const months = useMemo(() => {
    const source = data?.months ?? [];
    if (source.length === 12) return source;
    const empty = buildEmptyMonths();
    source.forEach((item) => {
      const index = Math.min(Math.max(item.month, 1), 12) - 1;
      empty[index] = item;
    });
    return empty;
  }, [data]);

  const totals = data?.totals ?? { budget: 0, actual: 0, diff: 0, executionPct: 0 };

  const chartData = useMemo(
    () =>
      months.map((m) => ({
        name: monthLabels[m.month - 1],
        Orcado: m.budget,
        Realizado: Math.abs(m.actual),
      })),
    [months]
  );

  const executionColor = (pctValue: number) => {
    if (pctValue >= 1) return "bg-danger";
    if (pctValue >= 0.8) return "bg-warning";
    return "bg-success";
  };

  return (
    <div className="container py-4">
      <h1 className="mb-3">Orcamento</h1>

      <div className="card shadow-sm mb-3" style={{ borderTop: "3px solid #0d6efd" }}>
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-1">
              <label className="form-label form-label-sm mb-1">Ano</label>
              <input
                className="form-control form-control-sm"
                value={year}
                onChange={(e) => setYear(normalizeYearInput(e.target.value))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label form-label-sm mb-1">Conta contabil</label>
              <select
                className="form-select form-select-sm"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value === "" ? "" : Number(e.target.value))}
              >
                <option value="">Todas as contas</option>
                {accounts.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label form-label-sm mb-1">Centro de custo</label>
              <select
                className="form-select form-select-sm"
                value={costCenterId}
                onChange={(e) => setCostCenterId(e.target.value === "" ? "" : Number(e.target.value))}
              >
                <option value="">Todos os centros</option>
                {costCenters.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-primary btn-sm" onClick={loadSummary}>
                Atualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <PageState loading={loading} error={error}>
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #6c757d" }}>
              <div className="card-body">
                <div className="text-muted small">Orcado no ano</div>
                <div className="fw-bold fs-5 mt-1">{formatMoneyBR(totals.budget)}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #0d6efd" }}>
              <div className="card-body">
                <div className="text-muted small">Realizado no ano</div>
                <div className="fw-bold fs-5 mt-1 text-primary">
                  {formatMoneyBR(Math.abs(totals.actual))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div
              className="card shadow-sm h-100"
              style={{ borderLeft: `4px solid ${totals.diff >= 0 ? "#198754" : "#dc3545"}` }}
            >
              <div className="card-body">
                <div className="text-muted small">Diferenca</div>
                <div
                  className={`fw-bold fs-5 mt-1 ${totals.diff >= 0 ? "text-success" : "text-danger"}`}
                >
                  {formatMoneyBR(totals.diff)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm h-100" style={{ borderLeft: "4px solid #fd7e14" }}>
              <div className="card-body">
                <div className="text-muted small">% Execucao</div>
                <div className="fw-bold fs-5 mt-1">{pct(totals.executionPct)}</div>
                <div className="progress mt-2" style={{ height: 6 }}>
                  <div
                    className={`progress-bar ${executionColor(totals.executionPct)}`}
                    style={{ width: `${Math.min(totals.executionPct * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {months.every((m) => m.budget === 0 && m.actual === 0) && (
          <div className="alert alert-warning">Sem dados para o periodo.</div>
        )}

        <div className="row g-3 mb-3">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="card-title">Orcado vs Realizado por mes</h6>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11 }} width={60} />
                    <Tooltip formatter={(value: number) => formatMoneyBR(value)} />
                    <Legend />
                    <Bar dataKey="Orcado" fill="#6c757d" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Realizado" fill="#0d6efd" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body">
            <h6 className="card-title">Detalhamento mensal</h6>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th></th>
                    <th>Mes</th>
                    <th>Orcado</th>
                    <th>Realizado</th>
                    <th>Diferenca</th>
                    <th style={{ minWidth: 140 }}>% Execucao</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((item) => {
                    const isExpanded = expandedMonth === item.month;
                    const details = detailsByMonth[item.month];
                    const monthActual = Math.abs(item.actual);
                    return (
                      <React.Fragment key={item.month}>
                        <tr>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              style={{ width: 28, padding: "0 4px" }}
                              onClick={() => {
                                if (isExpanded) {
                                  setExpandedMonth(null);
                                  return;
                                }
                                setExpandedMonth(item.month);
                                if (!detailsByMonth[item.month]) {
                                  void loadDetails(item.month);
                                }
                              }}
                            >
                              {isExpanded ? "−" : "+"}
                            </button>
                          </td>
                          <td className="fw-semibold">{monthLabels[item.month - 1]}</td>
                          <td>{formatMoneyBR(item.budget)}</td>
                          <td>{formatMoneyBR(monthActual)}</td>
                          <td className={item.diff < 0 ? "text-danger" : ""}>
                            {formatMoneyBR(item.diff)}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress flex-grow-1" style={{ height: 6 }}>
                                <div
                                  className={`progress-bar ${executionColor(item.executionPct)}`}
                                  style={{ width: `${Math.min(item.executionPct * 100, 100)}%` }}
                                />
                              </div>
                              <small style={{ whiteSpace: "nowrap" }}>{pct(item.executionPct)}</small>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <>
                            {detailsLoading[item.month] && (
                              <tr>
                                <td></td>
                                <td colSpan={5} className="text-muted">Carregando detalhes...</td>
                              </tr>
                            )}
                            {detailsError[item.month] && (
                              <tr>
                                <td></td>
                                <td colSpan={5} className="text-danger">{detailsError[item.month]}</td>
                              </tr>
                            )}
                            {!detailsLoading[item.month] &&
                              !detailsError[item.month] &&
                              details &&
                              details.categories.map((category) => {
                                const categoryActual = Math.abs(category.actual);
                                const categoryBudget = category.budget;
                                const categoryDiff = categoryActual - categoryBudget;
                                const categoryPct = categoryBudget
                                  ? categoryActual / categoryBudget
                                  : 0;
                                return (
                                  <React.Fragment key={`cat-${category.id ?? "null"}`}>
                                    <tr className="table-light">
                                      <td></td>
                                      <td className="ps-4 fw-semibold">{category.name}</td>
                                      <td>{formatMoneyBR(categoryBudget)}</td>
                                      <td>{formatMoneyBR(categoryActual)}</td>
                                      <td className={categoryDiff > 0 ? "text-danger" : ""}>
                                        {formatMoneyBR(categoryDiff)}
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <div className="progress flex-grow-1" style={{ height: 4 }}>
                                            <div
                                              className={`progress-bar ${executionColor(categoryPct)}`}
                                              style={{ width: `${Math.min(categoryPct * 100, 100)}%` }}
                                            />
                                          </div>
                                          <small>{pctFormatter.format(categoryPct)}</small>
                                        </div>
                                      </td>
                                    </tr>
                                    {category.accounts.map((account) => {
                                      const accountLabel = account.code
                                        ? `${account.code} - ${account.name}`
                                        : account.name;
                                      const accountActual = Math.abs(account.actual);
                                      const accountBudget = account.budget;
                                      const accountDiff = accountActual - accountBudget;
                                      const accountPct = accountBudget
                                        ? accountActual / accountBudget
                                        : 0;
                                      return (
                                        <tr key={`acc-${category.id ?? "null"}-${account.id ?? "null"}`}>
                                          <td></td>
                                          <td className="ps-5 text-muted">{accountLabel}</td>
                                          <td>{formatMoneyBR(accountBudget)}</td>
                                          <td>{formatMoneyBR(accountActual)}</td>
                                          <td className={accountDiff > 0 ? "text-danger" : ""}>
                                            {formatMoneyBR(accountDiff)}
                                          </td>
                                          <td>
                                            <small className="text-muted">
                                              {pctFormatter.format(accountPct)}
                                            </small>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            {!detailsLoading[item.month] &&
                              !detailsError[item.month] &&
                              details &&
                              details.categories.length === 0 && (
                                <tr>
                                  <td></td>
                                  <td colSpan={5} className="text-muted">Sem detalhes para o mes.</td>
                                </tr>
                              )}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </PageState>
    </div>
  );
};
